import type { PlainMessage } from "@bufbuild/protobuf";
import type { Timestamp } from "@bufbuild/protobuf";

/**
 * タイムスタンプをコンパレーション可能な形式に変換
 */
export function timestampToDate(
  timestamp: PlainMessage<Timestamp> | undefined,
): Date | null {
  if (!timestamp) return null;

  // Timestampの内部実装に応じて適切に変換
  // seconds * 1000としているのは、JavaScriptのDateがミリ秒で、
  // Protocol BuffersのTimestampが秒とナノ秒を使用しているため
  const seconds = Number(timestamp.seconds);
  const nanos = timestamp.nanos || 0;
  return new Date(seconds * 1000 + nanos / 1000000);
}

/**
 * タイムスタンプの比較関数
 */
export function compareTimestamps(
  a: PlainMessage<Timestamp> | undefined,
  b: PlainMessage<Timestamp> | undefined,
): number {
  const dateA = timestampToDate(a);
  const dateB = timestampToDate(b);

  if (dateA && dateB) {
    return dateA.getTime() - dateB.getTime();
  }

  if (dateA) {
    return -1; // aだけ日付があれば前に
  }

  if (dateB) {
    return 1; // bだけ日付があれば後に
  }

  return 0; // どちらも日付がなければ同等
}

/**
 * 優先度比較の入力となる残高オブジェクトの最小要件。
 * UserBalanceWithFundSourceベースの型・FormBalance型のどちらも満たす。
 * storeFilterCount/cityFilterCountは呼び出し元で計算済みであることを必須とする
 * （未計算のまま「制限なし」とみなされて優先度がずれる事故を防ぐため）。
 */
export type BalancePriorityItem = {
  userBalance?: {
    amount?: bigint | number;
    expireAt?: PlainMessage<Timestamp>;
  };
  fundSource?: {
    priorityWeight?: bigint | number;
  };
  storeFilterCount: number;
  cityFilterCount: number;
};

/**
 * ポイントの利用優先度を比較する関数。
 * sortBalanceWithFundSources と calculate-balances.ts の両方から共通で利用される。
 *
 * 優先順位:
 * 1. 有効期限が近いものを先に
 * 2. 利用可能店舗の個別選択数が少ないものを先に（制限なし=無限大として最も低優先）
 * 3. 特定地域のみ利用可の設定数が少ないものを先に（制限なし=無限大として最も低優先）
 * 4. ポイント残高が少ないものを先に
 * 5. priorityWeightが高いものを先に
 */
export function compareBalanceItemPriority(
  a: BalancePriorityItem,
  b: BalancePriorityItem,
): number {
  // 1. 有効期限が近いものを先に
  const compareExpireResult = compareTimestamps(
    a.userBalance?.expireAt,
    b.userBalance?.expireAt,
  );
  if (compareExpireResult !== 0) return compareExpireResult;

  // 2. 利用可能店舗の個別選択数が少ないものを先に
  if (a.storeFilterCount !== b.storeFilterCount) {
    return a.storeFilterCount - b.storeFilterCount;
  }

  // 3. 特定地域のみ利用可の設定数が少ないものを先に
  if (a.cityFilterCount !== b.cityFilterCount) {
    return a.cityFilterCount - b.cityFilterCount;
  }

  // 4. ポイント残高が少ないものを先に
  const aAmount = Number(a.userBalance?.amount) || 0;
  const bAmount = Number(b.userBalance?.amount) || 0;
  if (aAmount !== bAmount) {
    return aAmount - bAmount;
  }

  // 5. priorityWeightが高いものを先に（降順）
  return (
    (Number(b.fundSource?.priorityWeight) || 0) -
    (Number(a.fundSource?.priorityWeight) || 0)
  );
}
